/**
 * "Approve all" and "Approve selected" for the routine change requests, at
 * parity with the management app.
 *
 * 1. Opening checks the approval preview of each request first (at most
 *    `BATCH_APPROVE_LIMIT` per run, three at a time). A request whose preview
 *    has a blocker, or could not be loaded, is left out and moves to "Needs
 *    your review" with the reason: approving it would fail, and the backend
 *    does not re-check blockers when the transition runs.
 * 2. One confirm dialog lists what it will approve (child, request, change),
 *    what it leaves out and why, and how many wait for the next run.
 * 3. The batch approves the rest and re-reads each item, because an apply
 *    error does not fail the approve call.
 * 4. One toast: "5 approved", or "3 of 5 applied, 2 need your attention" with
 *    the names of the requests that need attention and "Show" for the first.
 *    A request someone else handled first is named apart, not as a problem.
 */

import { useCallback, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { change_requests_ApprovalPreviewResponse } from '@/api/models/change_requests_ApprovalPreviewResponse';
import { useBatchApprove, type BatchApproveSummary } from '@/hooks/useWorkflows';
import { useFetchApprovalPreview } from '@/hooks/useApprovalPreview';
import { useResolveServiceName } from '@/hooks/useResolveServiceName';
import { mapLimited } from '@/lib/mapLimited';
import { getRelationshipTypeLabel } from '@/lib/relationship/utils';
import type { RelationshipType } from '@/types/childContactRelationship';
import type { WorkflowItemResponse } from '@/types/workflow';
import {
  getBatchApproveBlockReason,
  getChangeRequestTypeLabel,
  isBatchApprovableChangeRequest,
  parseChangeRequestPayload,
} from '@/types/changeRequest';
import { previewLineText, type PreviewLine } from './approvalPreviewText';
import { buildChangeRequestSubjectText } from './changeRequestSubjectLine';

/** Requests one run checks and approves at most; the rest wait for the next run. */
export const BATCH_APPROVE_LIMIT = 50;
const PREVIEW_CONCURRENCY = 3;
// Long enough to read the names of the requests that need attention.
const PROBLEM_TOAST_DURATION_MS = 12000;

type Item = WorkflowItemResponse & { id: string };

interface ReadyItem {
  item: Item;
  preview: change_requests_ApprovalPreviewResponse;
}

interface HeldBackItem {
  item: Item;
  /** The first blocker, or `null` when the preview could not be loaded. */
  blocker: PreviewLine | null;
}

interface BatchPlan {
  checking: boolean;
  /** Opened from the ticked rows ("Approve selected"), not from "Approve all". */
  fromSelection: boolean;
  ready: ReadyItem[];
  heldBack: HeldBackItem[];
  later: number;
}

export interface BatchApproveFlow {
  /**
   * Checks the previews of these items and opens the confirm dialog. Pass
   * `fromSelection` when the items are the ticked rows, so the confirm button
   * repeats "Approve selected".
   */
  open: (items: Item[], options?: { fromSelection?: boolean }) => void;
  /** The confirm dialog; render it once on the page. */
  dialog: ReactElement | null;
  /** True from the click until the batch has run. */
  busy: boolean;
  /**
   * Requests the last check held back with their first blocker (`null`: no
   * preview); they belong under "Needs your review". Render the reason with
   * {@link HeldBackReason}.
   */
  heldBack: ReadonlyMap<string, PreviewLine | null>;
}

/** Who the request is about ("Mia Meier · by Anna Meier"), or its type when nobody is named. */
export function batchItemName(i18n: I18n, item: Item): string {
  const view = parseChangeRequestPayload(item.payload);
  return buildChangeRequestSubjectText(i18n, view, item.change_request_subject) || getChangeRequestTypeLabel(i18n, view);
}

const previewUnavailable = msg({
  id: 'workflow.batch.previewUnavailable',
  message: 'Preview unavailable.',
});

/** The preview's text context; resolves service names from the billing settings. */
function usePreviewTextContext() {
  const { i18n } = useLingui();
  const resolveServiceName = useResolveServiceName();
  return useMemo(
    () => ({
      i18n,
      serviceLabel: (code: string) => resolveServiceName(code),
      relationshipLabel: (type: string) => getRelationshipTypeLabel(type as RelationshipType, i18n),
    }),
    [i18n, resolveServiceName],
  );
}

/** Why "Approve all" left a request out: its first blocker as a sentence. */
export function HeldBackReason({ blocker }: { blocker: PreviewLine | null }) {
  const ctx = usePreviewTextContext();
  return <>{blocker ? previewLineText(blocker, ctx) : ctx.i18n._(previewUnavailable)}</>;
}

export function useBatchApproveFlow({ onShowItem }: { onShowItem: (itemId: string) => void }): BatchApproveFlow {
  const { i18n } = useLingui();
  const batch = useBatchApprove();
  const fetchPreview = useFetchApprovalPreview();
  const [plan, setPlan] = useState<BatchPlan | null>(null);
  const [heldBack, setHeldBack] = useState<ReadonlyMap<string, PreviewLine | null>>(new Map());
  // A newer `open` or a close makes an older preview check stale.
  const checkRef = useRef(0);

  const close = useCallback(() => {
    checkRef.current += 1;
    setPlan(null);
  }, []);

  const open = useCallback(
    (items: Item[], options?: { fromSelection?: boolean }) => {
      if (items.length === 0) return;
      const run = checkRef.current + 1;
      checkRef.current = run;
      const checked = items.slice(0, BATCH_APPROVE_LIMIT);
      const later = items.length - checked.length;
      const fromSelection = options?.fromSelection ?? false;
      setPlan({ checking: true, fromSelection, ready: [], heldBack: [], later });
      void mapLimited(checked, PREVIEW_CONCURRENCY, async (item) => {
        try {
          return { item, preview: await fetchPreview(item.id) };
        } catch {
          return { item, preview: undefined };
        }
      }).then((results) => {
        if (checkRef.current !== run) return;
        const ready: ReadyItem[] = [];
        const held: HeldBackItem[] = [];
        for (const { item, preview } of results) {
          if (!preview) {
            held.push({ item, blocker: null });
          } else if (preview.supported && preview.blockers.length > 0) {
            held.push({ item, blocker: preview.blockers[0] });
          } else {
            ready.push({ item, preview });
          }
        }
        setPlan({ checking: false, fromSelection, ready, heldBack: held, later });
        // Held-back requests stay marked until the next check, so the page
        // sorts them under "Needs your review" with their reason.
        setHeldBack(new Map(held.map(({ item, blocker }) => [item.id, blocker])));
      });
    },
    [fetchPreview],
  );

  const report = useCallback(
    (summary: BatchApproveSummary, ready: ReadyItem[]) => {
      // A request someone else handled first is neither done by this run nor a problem.
      const total = ready.length - summary.alreadyHandled.length;
      const problems = [...summary.notApplied, ...summary.failed];
      const raced =
        summary.alreadyHandled.length > 0
          ? i18n._({
              id: 'workflow.batch.alreadyHandled',
              message:
                '{count, plural, one {# request was already handled by someone else.} other {# requests were already handled by someone else.}}',
              values: { count: summary.alreadyHandled.length },
            })
          : undefined;
      // Someone else handled every request first: nothing was approved here,
      // so no green "0 approved".
      if (problems.length === 0 && summary.applied.length === 0 && raced) {
        toast.info(raced);
        return;
      }
      if (problems.length === 0) {
        toast.success(
          i18n._({
            id: 'workflow.triage.batchResult',
            message: '{count, plural, one {# approved} other {# approved}}',
            values: { count: summary.applied.length },
          }),
          raced ? { description: raced } : undefined,
        );
        return;
      }
      const byId = new Map(ready.map(({ item }) => [item.id, item]));
      const names = problems.map((id) => {
        const item = byId.get(id);
        return item ? batchItemName(i18n, item) : id;
      });
      toast.error(
        i18n._({
          id: 'workflow.batch.partialApplied',
          message:
            '{done} of {total} applied, {problems, plural, one {# needs your attention} other {# need your attention}}',
          values: { done: summary.applied.length, total, problems: problems.length },
        }),
        {
          description: raced ? (
            <>
              <span className="block">{names.join('; ')}</span>
              <span className="block">{raced}</span>
            </>
          ) : (
            names.join('; ')
          ),
          duration: PROBLEM_TOAST_DURATION_MS,
          action: {
            label: i18n._({ id: 'workflow.batch.show', message: 'Show' }),
            onClick: () => onShowItem(problems[0]),
          },
        },
      );
    },
    [i18n, onShowItem],
  );

  const confirm = useCallback(async () => {
    if (!plan || plan.checking || plan.ready.length === 0) return;
    const ready = plan.ready;
    const summary = await batch.mutateAsync(ready.map(({ item }) => item.id));
    report(summary, ready);
  }, [plan, batch, report]);

  const dialog = plan ? (
    <BatchApproveDialog plan={plan} onClose={close} onConfirm={confirm} isPending={batch.isPending} />
  ) : null;

  return { open, dialog, busy: batch.isPending || !!plan?.checking, heldBack };
}

interface BatchApproveDialogProps {
  plan: BatchPlan;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isPending: boolean;
}

function BatchApproveDialog({ plan, onClose, onConfirm, isPending }: BatchApproveDialogProps) {
  const { i18n } = useLingui();
  const textContext = usePreviewTextContext();
  const count = plan.ready.length;

  const changeText = (item: Item, preview: change_requests_ApprovalPreviewResponse): string => {
    const first = preview.supported ? preview.actions[0] : undefined;
    if (first) return previewLineText(first, textContext);
    const comment = (parseChangeRequestPayload(item.payload)?.executor?.body?.comment as { value?: string } | undefined)
      ?.value;
    return comment ?? '';
  };

  const line = (item: Item, detail: ReactNode, tone: 'muted' | 'warning') => {
    const view = parseChangeRequestPayload(item.payload);
    const subject = buildChangeRequestSubjectText(i18n, view, item.change_request_subject);
    const type = getChangeRequestTypeLabel(i18n, view);
    return (
      <li key={item.id} className="space-y-0.5 py-2" data-testid={`batch-approve-item-${item.id}`}>
        <p className="text-sm font-medium">{subject || type}</p>
        {subject && <p className="text-xs text-muted-foreground">{type}</p>}
        {detail && (
          <p className={tone === 'warning' ? 'text-xs text-warning-on-surface' : 'text-xs text-muted-foreground'}>
            {detail}
          </p>
        )}
      </li>
    );
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      className="sm:max-w-xl"
      title={
        plan.checking
          ? i18n._({ id: 'workflow.batch.checking', message: 'Checking the requests...' })
          : i18n._({
              id: 'workflow.batch.title',
              message: '{count, plural, one {Approve # request?} other {Approve # requests?}}',
              values: { count },
            })
      }
      description={
        plan.checking
          ? undefined
          : i18n._({
              id: 'workflow.batch.body',
              message: 'Each change is applied immediately and cannot be undone.',
            })
      }
      confirmLabel={
        plan.fromSelection
          ? i18n._({
              id: 'workflow.triage.approveSelectedCount',
              message: 'Approve selected ({count})',
              values: { count },
            })
          : i18n._({
              id: 'workflow.batch.confirm',
              message: 'Approve all ({count})',
              values: { count },
            })
      }
      confirmDisabled={plan.checking || count === 0}
      isPending={isPending}
      onConfirm={onConfirm}
    >
      {plan.checking ? (
        <div className="flex justify-center py-6" data-testid="batch-approve-checking">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : (
        <div className="space-y-4">
          {count > 0 && (
            <ul className="divide-y" data-testid="batch-approve-list">
              {plan.ready.map(({ item, preview }) => line(item, changeText(item, preview), 'muted'))}
            </ul>
          )}
          {plan.heldBack.length > 0 && (
            <div className="space-y-1" data-testid="batch-approve-held-back">
              <p className="text-sm font-medium">
                {i18n._({
                  id: 'workflow.batch.heldBack',
                  message:
                    '{count, plural, one {Needs your review, stays in the list (#):} other {Need your review, stay in the list (#):}}',
                  values: { count: plan.heldBack.length },
                })}
              </p>
              <ul className="divide-y">
                {plan.heldBack.map(({ item, blocker }) => line(item, <HeldBackReason blocker={blocker} />, 'warning'))}
              </ul>
            </div>
          )}
          {plan.later > 0 && (
            <p className="text-sm text-muted-foreground" data-testid="batch-approve-later">
              {i18n._({
                id: 'workflow.batch.later',
                message:
                  '{count, plural, one {# more request waits for the next run.} other {# more requests wait for the next run.}}',
                values: { count: plan.later },
              })}
            </p>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

/**
 * Why this request is not part of "Approve all", or `null` when it is (or when
 * batching does not apply): a blocker from the last check first, else the rule
 * that sends its kind, source or match to "Needs your review".
 */
export function batchLockReason(
  i18n: I18n,
  item: Item,
  heldBack: ReadonlyMap<string, PreviewLine | null>,
): ReactNode | null {
  if (item.current_state === 'pending' && heldBack.has(item.id)) {
    return <HeldBackReason blocker={heldBack.get(item.id) ?? null} />;
  }
  return getBatchApproveBlockReason(i18n, item);
}

/** True when "Approve all" may take this request: batchable by its kind and not held back. */
export function isInBatch(item: Item, heldBack: ReadonlyMap<string, PreviewLine | null>): boolean {
  return isBatchApprovableChangeRequest(item) && !heldBack.has(item.id);
}
